// Base API client with authentication, error handling, and transparent token refresh

import type { ApiError } from '../types/api';
import type { RefreshTokenResponse } from '../types/auth';
import { clearAuth, getRefreshToken, setToken, setRefreshToken, setExpiresAt, setRefreshExpiresAt, setPermission } from '../utils/storage';
import { parsePermission } from '../types/auth';

const API_URL = process.env.API_URL || 'http://localhost:8080';

// Shared promise to prevent concurrent refresh calls
let refreshPromise: Promise<RefreshTokenResponse> | null = null;

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Uses a shared promise so concurrent 401s don't trigger multiple refreshes.
 */
async function refreshAccessToken(baseUrl: string): Promise<RefreshTokenResponse> {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  refreshPromise = fetch(`${baseUrl}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error('Refresh failed');
    }
    return response.json() as Promise<RefreshTokenResponse>;
  }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

/**
 * Generic API client for making authenticated HTTP requests
 */
export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Make an authenticated HTTP request
   *
   * @param endpoint - API endpoint (e.g., '/auth/login')
   * @param options - Fetch options (method, body, headers)
   * @returns Promise with typed response data
   */
  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Get JWT token from localStorage
    const token = localStorage.getItem('aokfin_auth_token');

    // Build headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add Authorization header if token exists
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    // Make request
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle errors
    if (!response.ok) {
      // On 401 for non-auth endpoints, try to refresh the token
      if (response.status === 401 && !endpoint.startsWith('/api/auth/')) {
        try {
          const refreshResult = await refreshAccessToken(this.baseUrl);

          // Store new tokens
          setToken(refreshResult.token);
          setRefreshToken(refreshResult.refresh_token);
          setExpiresAt(new Date(refreshResult.expires_at));
          setRefreshExpiresAt(new Date(refreshResult.refresh_expires_at));
          if (refreshResult.permission) {
            try {
              setPermission(parsePermission(refreshResult.permission));
            } catch {
              // permission string didn't parse — keep existing
            }
          }

          // Retry the original request with the new token
          (headers as Record<string, string>)['Authorization'] = `Bearer ${refreshResult.token}`;
          const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers,
          });

          if (!retryResponse.ok) {
            const retryError: ApiError = await retryResponse.json().catch(() => ({
              message: `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
            }));
            throw new Error(retryError.message || 'An error occurred');
          }

          return retryResponse.json();
        } catch {
          // Refresh failed — clear auth and redirect to login
          clearAuth();
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }
      }

      // Parse error response
      const error: ApiError = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));

      throw new Error(error.message || 'An error occurred');
    }

    // Parse successful response
    return response.json();
  }

  /**
   * GET request
   */
  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
